import { IRequestContext, IRequestContextUser } from '@msdyn365-commerce/core-internal';
import { Channel, User, UserFactory } from '@relewise/client';
import { CookieNames, ConsentGatedCookieNames } from '../../../shared/services/cookie-service/cookie-service.const';
import { CookieService } from '../../../shared/services/cookie-service/cookie-service';

const DEVICE_COOKIE_VALUE_FOR_MOBILE_APP = 'webview';

const APP_CLIENT_NAME = 'App';

export class RelewiseUserService {
    protected request: IRequestContext;
    protected authUser: IRequestContextUser | undefined;
    protected userAgent: string | undefined;
    protected countryIsoCode: string | null;
    private cookieService: CookieService;

    constructor(request: IRequestContext) {
        this.request = request;
        this.authUser = request.user;
        this.countryIsoCode = request.channel?.ChannelCountryRegionISOCode ?? null;
        this.userAgent = typeof navigator === 'undefined' ? request.headers['user-agent'] : navigator.userAgent;
        this.cookieService = new CookieService(request);
    }

    private async getTemporaryUserId() {
        return this.cookieService.getConsentedControllerId(ConsentGatedCookieNames.Relewise);
    }

    private async buildUnauthenticatedUser() {
        const temporaryId = await this.getTemporaryUserId();

        return !!temporaryId ? UserFactory.byTemporaryId(temporaryId) : UserFactory.anonymous();
    }

    private async buildAuthenticatedUser() {
        const { isAuthenticated, customerAccountNumber, emailAddress } = this.authUser ?? {};
        if (!isAuthenticated || !customerAccountNumber || !emailAddress) {
            return;
        }

        const temporaryId = await this.getTemporaryUserId();
        return UserFactory.byAuthenticatedId(customerAccountNumber, temporaryId, {
            email: emailAddress
        });
    }

    private getClientCookieValue() {
        return CookieService.getRequestCookie(CookieNames.Client, this.request);
    }

    private isMobileApp() {
        return this.getClientCookieValue() === DEVICE_COOKIE_VALUE_FOR_MOBILE_APP;
    }

    private isLikelyIPadInDesktopMode(ua: string) {
        return /Macintosh/i.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1;
    }

    private getDeviceType() {
        if (this.isMobileApp()) {
            return 'Phone';
        }

        const ua = this.userAgent;
        if (!ua) {
            return 'Unknown';
        }

        const isIPhone = /iPhone/i.test(ua);
        const isIPad = /iPad/i.test(ua);
        const isAndroid = /Android/i.test(ua);
        const isAndroidMobile = isAndroid && /Mobile/i.test(ua);
        const isLikelyIPadDesktopMode = this.isLikelyIPadInDesktopMode(ua);

        if (isIPhone || isAndroidMobile) {
            return 'Phone';
        }

        if (isIPad || isLikelyIPadDesktopMode) {
            return 'Tablet';
        }

        if (isAndroid) {
            return 'Tablet';
        }

        return 'Desktop';
    }

    private getPlatform() {
        const ua = this.userAgent;
        if (!ua) {
            return 'Unknown';
        }

        if (/iPhone|iPad/i.test(ua)) {
            return 'iOS';
        }
        if (/Android/i.test(ua)) {
            return 'Android';
        }
        if (this.isLikelyIPadInDesktopMode(ua)) {
            return 'iOS';
        }
        if (/Mac/i.test(ua)) {
            return 'macOS';
        }
        if (/Windows/i.test(ua)) {
            return 'Windows';
        }
        if (/Linux/i.test(ua)) {
            return 'Linux';
        }

        return 'Unknown';
    }

    private getUserChannel(): Channel | undefined {
        const deviceType = this.getDeviceType();
        const platform = this.getPlatform();

        const clientCookieValue = this.getClientCookieValue();
        const clientName = clientCookieValue === DEVICE_COOKIE_VALUE_FOR_MOBILE_APP ? APP_CLIENT_NAME : clientCookieValue;

        return {
            name: deviceType,
            subChannel: {
                name: platform,
                ...(clientName
                    ? {
                          subChannel: {
                              name: clientName
                          }
                      }
                    : {})
            }
        };
    }

    private getUserClassifications(): Record<string, string | null> | undefined {
        return {
            country: this.countryIsoCode
        };
    }

    public async getUser() {
        let user: User;

        const authenticatedUser = await this.buildAuthenticatedUser();
        if (authenticatedUser) {
            user = authenticatedUser;
        } else {
            user = await this.buildUnauthenticatedUser();
        }

        user.channel = this.getUserChannel();
        user.classifications = this.getUserClassifications();

        return user;
    }
}
